import { useCallback, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { getUserInfo } from '../api/api';
import { HttpStatus } from '../api/http';

const requestError = 'Failed to verify that you have access to Synergi Life AI.';
const accessDeniedError =
  "You don't have access to Synergi Life AI. Please contact your administrator.";

export interface UserInfo {
  aiCaseAccess: boolean;
}

interface UserInfoContext {
  isAuthorized: boolean;
  userInfoError: string | undefined;
  data: UserInfo | undefined;
}

export function useUserInfo(): UserInfoContext {
  const auth = useAuth();
  const isAuthenticated = auth.isAuthenticated && !auth.isLoading && !auth.error;

  const [isAuthorized, setIsAuthorized] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [userInfoError, setUserInfoError] = useState<string | undefined>();
  const [data, setData] = useState<UserInfo | undefined>();

  useEffect(() => {
    if (isAuthenticated && !isLoading && !isAuthorized && !userInfoError) {
      const accessToken = auth?.user?.access_token ?? '';
      fetchUserInfo(accessToken);
    }
  }, [isAuthenticated, isLoading, isAuthorized]);

  const fetchUserInfo = useCallback(async (accessToken: string) => {
    setIsLoading(true);

    const response = await getUserInfo({ accessToken });

    if (response.status === HttpStatus.Unauthorized || response.status === HttpStatus.Forbidden) {
      setUserInfoError(accessDeniedError);
    } else if (response.status !== HttpStatus.OK) {
      setUserInfoError(requestError);
    } else {
      const userInfo = await response.json();
      setIsAuthorized(true);
      setData(userInfo);
    }

    setIsLoading(false);
  }, []);

  return { isAuthorized, userInfoError, data };
}
